import { ReplyUser } from '@src/api/shared/community/chatApiClient';
import DefaultAvatar from '@src/assets/images/avatar/default.png';
import { THREAD_PANEL_SHRINK_THRESHOLD } from '@src/features/shared/community/constants/chatBoxThreadPanelResize';
import useIsMobile from '@src/hooks/useIsMobile';
import React, { useEffect, useState } from 'react';

interface ThreadLinkProps {
  replyCount: number;
  replyUsers?: ReplyUser[];
  chatBoxWidth: number;
  lastReplyTime?: string;
  onClick: () => void;
  className?: string;
}

const ThreadLink: React.FC<ThreadLinkProps> = ({
  replyCount,
  replyUsers = [],
  chatBoxWidth,
  lastReplyTime,
  onClick,
  className = '',
}) => {
  const [formattedLastReplyTime, setFormattedLastReplyTime] = useState<string>('');
  const isMobile = useIsMobile();

  useEffect(() => {
    setFormattedLastReplyTime(
      isMobile || chatBoxWidth >= THREAD_PANEL_SHRINK_THRESHOLD.small
        ? `最終返信: ${lastReplyTime}`
        : '最終返信...',
    );
  }, [chatBoxWidth, lastReplyTime, isMobile]);

  if (replyCount === 0) {
    return null;
  }

  const MAX_AVATARS = 5;
  const MAX_REMAINING_DISPLAY = 99;

  const displayUsers = replyUsers.slice(0, MAX_AVATARS);
  const remainingCount = Math.max(0, replyUsers.length - MAX_AVATARS);
  const formattedRemainingCount =
    remainingCount > MAX_REMAINING_DISPLAY ? MAX_REMAINING_DISPLAY : remainingCount;

  return (
    <div
      className={`ml-10 flex items-center gap-4 rounded py-1 pl-1 pr-2 text-sm transition-colors duration-200 md:ml-0 md:pl-0 sm:gap-3 ${className}`}
      aria-label={`${replyCount}件の返信を表示`}
    >
      {displayUsers.length > 0 && (
        <div className='flex items-center gap-2'>
          {displayUsers.map((user, index) => (
            <div
              key={index}
              className='relative size-7 shrink-0 overflow-hidden rounded-full'
            >
              <img
                src={user.avatar_url || DefaultAvatar}
                alt={user.display_name || ''}
                className='size-full object-cover'
              />

              {/* Show +X if there are more users */}
              {remainingCount > 0 && index === displayUsers.length - 1 && (
                <div className='absolute left-0 top-0 flex size-full shrink-0 items-center justify-center rounded-full bg-gray-400/75'>
                  <span className='text-[11px] font-medium text-white'>
                    +{formattedRemainingCount}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reply count and last reply time */}
      <div className='flex items-center gap-[5px]'>
        <button
          type='button'
          onClick={onClick}
          className='cursor-pointer text-sm font-medium text-blue-600 hover:underline sm:text-xs'
        >
          {replyCount}件の返信
        </button>
        {lastReplyTime && (
          <span className='pt-[2px] text-xs sm:text-[10px]'>{formattedLastReplyTime}</span>
        )}
      </div>
    </div>
  );
};

export default ThreadLink;
